import type { IRequestOpts, IResultstash, JsonQuery } from "@mne-core/api";
import { QueryController, createPostRequestOpts, createUrl, fetchJsonQuery } from "@mne-core/api";

import type { IAuthenticationData } from "../@types";

/**
 * Interface representing the criteria for WebAuthn authentication.
 *
 * Requires WebAuthn authentication to be enabled on the server. Use the `/methods` endpoint to check which
 * authentication methods are available on the server. The client can optionally request specific
 * authorizations as part of the authentication transaction, though these authorizations are not
 * guaranteed to be granted. The `setPassword` authorization, is only returned if
 * the current user has the `FORCE_PW_CHANGE` flag set.
 *
 *
 * @interface IWebAuthnAuthenticateCriteria
 * @property {Array<TRequestedAuthorizationOptions>} [requestedAuthorizations] - Optional. Allows the client to request
 *           authorizations as part of the authentication transaction.
 * @property {string} userName - The username to authenticate.
 * @property {string} domain - The domain to which the user belongs.
 * @property {string} webauthnKeyID - The WebAuthn key ID of the selected webauthn key.
 * @property {string} challengeID - The challenge ID returned from the prepareWebauthn request.
 * @property {string} authenticatorData - The authenticator data structure from the WebAuthn invocation.
 * @property {string} clientData - The clientDataJSON structure from the WebAuthn invocation. .
 * @property {string} signature - The signature structure from the WebAuthn invocation.
 */

type TRequestedAuthorizationOptions = "enrollPublicKey" | "setPassword";

export interface IWebAuthnAuthenticateCriteria {
  requestedAuthorizations?: Array<TRequestedAuthorizationOptions>;
  userName: string;
  domain: string;
  webauthnKeyID: string;
  challengeID: string;
  authenticatorData: string;
  clientData: string;
  signature: string;
}

export const createRequest = (criteria: IWebAuthnAuthenticateCriteria, opts: IRequestOpts): Request => {
  const { apiUrl } = opts;
  const uri = createUrl(apiUrl, ["authentication", "v1", "signature", "webauthn", "authentication"]).toString();
  return new Request(uri, createPostRequestOpts(JSON.stringify(criteria), opts));
};

async function authenticateWebAuthn(
  criteria: IWebAuthnAuthenticateCriteria,
  opts: IRequestOpts,
): Promise<JsonQuery<IResultstash<IAuthenticationData>>> {
  return await fetchJsonQuery<IResultstash<IAuthenticationData>>(createRequest(criteria, opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}

export default authenticateWebAuthn;
